/**
 * Single source of truth for company, contact, address, social, and external service URLs.
 * Consumed by `marketing/` and `portal/` workspaces. Keep framework-agnostic (no React/Next imports).
 */
export const siteConfig = {
  // Identity
  name: 'SightHost',
  legalName: 'SightHost (Pty) Ltd',
  registration: '2018/238217/07',
  vatNumber: '',
  foundingDate: '2018',

  /**
   * Corporate parent. SightHost (Pty) Ltd is a subsidiary of Hindsight
   * Consulting (Pty) Ltd — a South African web development business. Surface
   * this on the About page so the group relationship is transparent to
   * customers.
   *
   * NOT "wholly-owned" (owner instruction 2026-09-01): SightHost ceases to be
   * wholly owned on 2026-09-15, when 10% issues to its incoming CEO. Under
   * Companies Act s3(1)(a) a subsidiary turns on control of a majority of the
   * voting rights, so the word stays accurate at 90%. `relationship` exists so
   * that the phrase has ONE definition — AboutPage.tsx previously hardcoded
   * "wholly-owned subsidiary" in JSX while reading every other fact from here,
   * which is how the claim survived in a place nobody thought to look.
   */
  parent: {
    name: 'Hindsight Consulting (Pty) Ltd',
    shortName: 'Hindsight Consulting',
    /** How SightHost relates to its parent. Reads as "<legalName> is <relationship> of <name>". */
    relationship: 'a subsidiary',
    /** Hindsight Consulting's own web presence — currently the hindsight.africa surface. */
    url: 'https://hindsight.africa',
    description:
      'Hindsight Consulting (Pty) Ltd is a South African web development business and the parent company of SightHost.',
  },

  /**
   * Group + industry affiliations rendered on the About page. Add new entries
   * here rather than hard-coding in AboutPage.tsx.
   *
   * Sight Business Solutions is the bookkeeping + HR platform hosted at
   * hindsight.africa. SightHost hosts the infrastructure under the group;
   * the relationship is a real cross-group case study, not a paid
   * partnership.
   */
  affiliations: [
    {
      name: 'Sight Business Solutions',
      url: 'https://hindsight.africa',
      relationship: 'sister platform',
      description:
        'A bookkeeping + HR platform for South African SMEs, hosted at hindsight.africa. SightHost provides the underlying hosting infrastructure.',
    },
  ] as ReadonlyArray<{
    name: string;
    url: string;
    relationship: string;
    description: string;
  }>,

  /**
   * VAT registration status. Drives invoice header text ("INVOICE" vs "TAX INVOICE"),
   * default VAT rate on new line items, and whether SightHost's VAT number renders
   * on documents. See memory/vat_model.md for the full design.
   *
   * Switching to true requires:
   *   1. Set `vatRegistered: true`
   *   2. Set `vatNumber` above to the SARS-issued number
   *   3. Update `service_plan.vat_rate` rows to 0.15 via a data migration
   * No schema migration is required — the columns already exist with vat_rate=0.
   */
  vatRegistered: false as boolean,
  /** Default VAT rate applied to NEW invoice lines when `vatRegistered` is true. */
  defaultVatRate: 0.15,

  // Web — flipped to sighthost.online on 2026-05-25 per Phase D rebrand cutover.
  url: 'https://www.sighthost.online',
  domain: 'sighthost.online',

  // Description / SEO
  description:
    'Premium web hosting, domain registration & fiber internet in South Africa. Fast, reliable hosting from R129/mo with 99.9% uptime guarantee. POPIA compliant, local support.',
  tagline: 'Your trusted partner in the digital world',
  keywords: [
    'web hosting south africa',
    'south african web hosting',
    'hosting south africa',
    'sa web hosting',
    'johannesburg web hosting',
    'gauteng hosting provider',
    'domain registration south africa',
    'register .co.za domain',
    // Fibre keywords disabled 2026-05-08 — service is not yet active.
    // Restoring these now would mislead search engines + visitors. Re-enable
    // when the fibre service launches.
    // 'fiber internet south africa',
    // 'fibre installation johannesburg',
    'cpanel hosting',
    'email hosting south africa',
    'ssl certificates',
    'wordpress hosting south africa',
    'affordable web hosting',
    'reliable hosting',
    '99.9% uptime hosting',
    'ssd hosting south africa',
    'popia compliant hosting',
    'small business hosting',
    'professional hosting services',
  ],

  // Contact — flipped to sighthost.online on 2026-05-25 per Phase D rebrand cutover.
  // cPanel forwarders for support/info/billing/legal/privacy@sighthost.online route to
  // the existing inbox so replies still land in the same place during the overlap window.
  phone: '+27720261267',
  phoneDisplay: '072 026 1267',
  email: 'info@sighthost.online',
  supportEmail: 'support@sighthost.online',
  billingEmail: 'billing@sighthost.online',
  legalEmail: 'legal@sighthost.online',
  privacyEmail: 'privacy@sighthost.online',
  informationOfficer: {
    name: 'Information Officer',
    email: 'privacy@sighthost.online',
  },
  // Truthful-claims (2026-08-11): this said '24/7' while the business runs a
  // business-hour desk. Two things ARE genuinely around the clock — the portal
  // accepts tickets at any hour, and platform monitoring never sleeps — so say
  // those, and say them separately from staffed human support.
  // Confirmed by Mr Mampe 2026-08-11: 08:30-16:30, weekdays. This is the ONE
  // place these hours are written — /contact, /sla and the welcome email all
  // read from here rather than restating them.
  supportHours: 'Mon-Fri 08:30-16:30 (SAST)',
  supportHoursShort: 'Business hours (SAST)',

  // Address
  address: {
    street: 'Northgate Office Park',
    locality: 'Northriding',
    city: 'Randburg',
    region: 'Gauteng',
    postalCode: '2162',
    country: 'ZA',
    countryName: 'South Africa',
    coordinates: { lat: -26.0461, lng: 27.9568 },
  },

  // Social — only profiles that actually exist; add more here when opened
  social: {
    facebook: 'https://www.facebook.com/sighthost.online',
    linkedin: 'https://www.linkedin.com/company/sighthost/',
  },

  // Operations
  businessType: 'InternetServiceProvider',
  numberOfEmployees: { min: 2, max: 10 },
  paymentAccepted: ['Credit Card', 'Debit Card', 'EFT', 'PayFast'],
  currenciesAccepted: 'ZAR',
  priceRange: 'R129 - R499',

  /**
   * SightHost customer portal SPA on Cloudflare Pages.
   * - `productionUrl` is where the portal SPA is deployed.
   * - `devUrl` is the local Vite dev server for `pnpm dev:portal`.
   * - All marketing CTAs resolve through `marketing/src/config/portal.ts → getPortalUrl()`.
   * - Flipped to portal.sighthost.online on 2026-05-25 (Phase D cutover). The old
   *   portal.afrilinktelecon.co.za domain stays as a Cloudflare 301 redirect target
   *   per CLAUDE.md operational notes; in-flight email links keep working.
   */
  portal: {
    productionUrl: 'https://portal.sighthost.online',
    devUrl: 'http://localhost:5174',
    signInPath: '/sign-in',
    signUpPath: '/sign-up',
    dashboardPath: '/app',
    hostingPath: '/app/services',
    hostingOrderPath: '/app/services/new',
    domainsPath: '/app/domains',
    // Must match the portal route in portal/src/apps/client/AppRouter.tsx
    // ('domains/register'). Was '/app/domains/new' until 2026-07-02 — a dead
    // route; every marketing Domains-page CTA landed on the portal 404.
    domainRegisterPath: '/app/domains/register',
    domainTransferPath: '/app/domains/transfer',
    supportPath: '/app/support',
    submitTicketPath: '/app/support/new',
    knowledgeBasePath: '/app/knowledgebase',
    contactHandlerPath: '/api/marketing-contact',
  },

  /**
   * Public Supabase Edge Function that receives marketing contact-form
   * submissions and delivers them to support@afrilinktelecon.co.za via the
   * notification-runner. Replaces the legacy
   * billing.afrilinktelecon.co.za/contact-handler.php endpoint as of
   * 2026-05-04.
   */
  marketingContactEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/marketing-contact',

  /**
   * Public Edge Function that captures client-side JS errors from the
   * marketing site (M3.13). Marketing main.tsx wires window.onerror +
   * window.onunhandledrejection to POST here; rows land in marketing_error
   * for staff review via Supabase dashboard or admin reports later.
   */
  marketingErrorLogEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/marketing-error-log',

  /**
   * Public Edge Function for anonymous domain-availability checks (no JWT —
   * verify_jwt=false since 2026-07-01; per-IP rate-limited). Drives the
   * site-wide DomainSearch widget on the marketing site and the portal
   * order-funnel Step 2 badge. Real-time ZACR/registry answer — never cached.
   */
  domainAvailabilityEndpoint:
    'https://djnwjeslpomvornubmsv.supabase.co/functions/v1/domain-availability',

  /**
   * TLDs the DomainSearch widget offers, with retail pricing where self-serve
   * registration is live. Verified against the domains.co.za reseller API on
   * 2026-07-02 (all entries below returned real registry answers through the
   * production service identity). `selfServe: true` = pricing row exists in
   * `system_setting.domains_pricing` (migration `20260702083000` — owner set
   * the published marketing prices live), so the portal register funnel
   * completes; the unpriced tail is concierge-only (contact/ticket).
   */
  domainSearch: {
    /** Appended when the visitor types a bare name with no dot. */
    defaultTld: 'co.za',
    tlds: [
      // Corrected 2026-09-13 against live wholesale. Six of these were sold
      // BELOW COST — .capetown/.joburg/.durban at HALF cost (R149 vs R299).
      // Rule: .za family = max(R10, 10% of cost); everything else = 15% of cost,
      // rounded up to the next R_9. priceZar is the REGISTRATION price.
      // .africa is HELD above the rule at R299: its cost moved R99 -> R189 in
      // eight weeks during 2025, and the margin is the shock absorber.
      // Must stay in step with system_setting.domains_pricing (the authority)
      // and with marketing DomainsPage. See
      // _bmad-output/DOMAIN_WHOLESALE_PRICING_2026-09-13.md
      { tld: 'co.za',    priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'org.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'net.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'web.za',   priceZar: 99,   selfServe: true },  // cost 89.00
      { tld: 'com',      priceZar: 269,  selfServe: true },  // cost 229.88 -> 17.0%
      { tld: 'org',      priceZar: 289,  selfServe: true },  // cost 246.30 -> 17.3%
      { tld: 'net',      priceZar: 289,  selfServe: true },  // cost 246.30 -> 17.3%
      { tld: 'africa',   priceZar: 299,  selfServe: true },  // cost 189.00 -> 58.2%; renew cost 259.00 -> 15.4%
      { tld: 'capetown', priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'joburg',   priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'durban',   priceZar: 349,  selfServe: true },  // cost 299.00 -> 16.7%
      { tld: 'online',   priceZar: null, selfServe: false },
      { tld: 'store',    priceZar: null, selfServe: false },
      { tld: 'xyz',      priceZar: null, selfServe: false },
      { tld: 'io',       priceZar: null, selfServe: false },
    ] as ReadonlyArray<{ tld: string; priceZar: number | null; selfServe: boolean }>,
    /** Shown as one-tap chips under the hero search input. */
    featuredTlds: ['co.za', 'com', 'africa', 'capetown', 'online'],
  },

  /**
   * Cloudflare Turnstile — captcha for marketing forms (M2.10). Site key is
   * public-by-design (rendered in the browser); the matching SECRET key is
   * stored as TURNSTILE_SECRET_KEY in the marketing-contact Supabase Edge
   * Function env vars (NOT here, NOT committed). Provisioned 2026-05-07.
   */
  turnstile: {
    siteKey: '0x4AAAAAADK-U90WVxnFPQla',
    verifyUrl: 'https://challenges.cloudflare.com/turnstile/v0/siteverify',
  },

  /**
   * Google Ads conversion tracking (client-side, Consent Mode v2). Both values
   * are PUBLIC by design. Empty strings keep tracking FULLY INERT — no gtag.js
   * loads and no conversion fires — so the feature ships ahead of setup. The
   * owner pastes these in after creating the "Purchase" conversion action in
   * Google Ads (Conversion ID → conversionId; the label AFTER the slash in
   * send_to → purchaseLabel).
   * See _bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md.
   */
  googleAds: {
    /** Google tag / Conversion ID. Set 2026-07-09 — loads the tag site-wide. */
    conversionId: 'AW-18309451991' as string,
    /**
     * "Purchase" conversion label (part after the slash in send_to). From the
     * NATIVE manual Google Ads Purchase conversion action (Event → destination
     * AW-18309451991), set 2026-07-09. fireAdsPurchase now sends
     * 'AW-18309451991/5FJ4CLHgl80cENeh0JpE' on a confirmed-paid invoice.
     */
    purchaseLabel: '5FJ4CLHgl80cENeh0JpE' as string,
  },

  // Brand
  brand: {
    primary: '#F6960C',
    secondary: '#4A7C8C',
    accent: '#FFB347',
    // The browser-chrome colour must be the brand primary. It was #ea580c
    // (orange-600) — a fourth brand colour visible on every mobile address
    // bar, and the one surface where drift is impossible to miss. The two PWA
    // manifests paint the same value; marketing/public/manifest.json was still
    // on #ea580c until 2026-08-12 because it is not a .ts file and no gate
    // looked at it.
    themeColor: '#F6960C',
  },

  /**
   * Banking details. Rendered on every invoice (mirrors
   * hindsight-accounting-suite ProfessionalTemplate layout) and, since
   * 2026-08-12, on six marketing pages via BankDetailsPanel and in the agent
   * bank-confirmation letter.
   *
   * **This object has a second copy: `BANK` in
   * supabase/functions/_shared/brand.ts.** Edge Functions run on Deno and cannot
   * import this workspace, so the duplication is deliberate — change one, change
   * the other. (Before 2026-08-12 the email block did not even have that copy;
   * the account number was an inline JSX literal.)
   *
   * `accountName` must match the printed name on the Capitec bank statement
   * screen — if they drift, customer EFTs may be held for review. See
   * memory/sighthost_rebrand_in_progress.md.
   */
  bank: {
    name: 'Capitec Business Account',
    accountName: 'SightHost (Pty) Ltd',
    accountNumber: '1054752672',
    branchCode: '450105',
    accountType: 'Business',
    swiftCode: '',
    referenceHint: 'Use invoice number as reference',
  },
} as const;

export type SiteConfig = typeof siteConfig;
